import { motion } from "framer-motion";
import { Sparkles, Shield, DollarSign, Users, Clock, Award } from "lucide-react";

const reasons = [
  {
    icon: Sparkles,
    title: "Precise, Clean Installs",
    desc: "Every window and glass unit is measured exactly and installed clean — sealed, insulated, and weather-tight the first time.",
    accent: "primary" as const,
  },
  {
    icon: Shield,
    title: "Licensed & Insured",
    desc: "Inka Glass is fully insured, so your home and business are protected on every window and glass replacement we handle.",
    accent: "primary" as const,
  },
  {
    icon: DollarSign,
    title: "Honest, Transparent Pricing",
    desc: "Free written estimates with no hidden fees. You know exactly what your window or glass replacement costs before we start.",
    accent: "secondary" as const,
  },
  {
    icon: Clock,
    title: "Fast, Reliable Turnaround",
    desc: "Broken glass is a safety concern — we move quickly to measure, cut, and install so your home is secure again fast.",
    accent: "primary" as const,
  },
  {
    icon: Award,
    title: "Built for the Houston Climate",
    desc: "Energy-efficient, Low-E, and storm-rated glass options made for Gulf Coast heat, humidity, and hurricane season.",
    accent: "secondary" as const,
  },
  {
    icon: Users,
    title: "Local Houston Team",
    desc: "A locally owned glass company serving 14+ cities across the greater Houston area — friendly service you can count on.",
    accent: "primary" as const,
  },
];

const WhyChooseUs = () => {
  return (
    <section className="section-padding bg-accent relative overflow-hidden">
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      <div className="container-max relative z-10">
        <div className="text-center mb-14 md:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-secondary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Why Inka Glass
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-accent-foreground mb-4 tracking-tight font-bold"
          >
            Why Choose <span className="text-gradient-primary">Us</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-accent-foreground/70 max-w-2xl mx-auto text-lg"
          >
            Six reasons homes and businesses across the greater Houston area trust us for window and glass replacement.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reasons.map((reason, i) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="glass-card rounded-2xl p-6 md:p-8 card-hover-lift"
            >
              <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl mb-5 ${
                reason.accent === "secondary"
                  ? "bg-secondary/25 text-secondary"
                  : "bg-primary/25 text-primary-foreground"
              }`}>
                <reason.icon className="h-7 w-7" />
              </div>
              <h3 className="font-heading text-xl text-accent-foreground mb-2 tracking-tight font-bold">{reason.title}</h3>
              <p className="text-accent-foreground/70 text-sm leading-relaxed">{reason.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
